import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../shared/ConfirmDialog';
import { COLLECTIONS } from '../../constants/collections';
import { CONFIG_CATEGORIES } from '../../constants/systemConfig';
import { createDocument, getAllDocuments, updateDocument } from '../../services/firestore/firestoreService';
import { generateTestReservations } from '../../services/dev/dataGenerator';

// This service is embedded here for simplicity but could be in its own service file
const getGeneralSettings = async (tenantId) => {
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.GENERAL_SETTINGS }
    ];
    const results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);
    // Return first document if exists, otherwise null
    return results.length > 0 ? results[0] : null;
};

const saveGeneralSettings = async (tenantId, settingsId, data) => {
    if (settingsId) {
        // Update
        return await updateDocument(COLLECTIONS.SYSTEM_CONFIGS, settingsId, {
            ...data,
            updatedAt: new Date()
        });
    } else {
        // Create
        const payload = {
            ...data,
            category: CONFIG_CATEGORIES.GENERAL_SETTINGS,
            tenantId,
            createdAt: new Date(),
            updatedAt: new Date(),
            isActive: true
        };
        return await createDocument(COLLECTIONS.SYSTEM_CONFIGS, payload);
    }
};

const SystemParametersConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [existingId, setExistingId] = useState(null);

    const handleGenerateData = async () => {
        showConfirm({
            title: 'Test Verisi Oluştur',
            message: 'Bu işlem sisteme 10 adet rastgele rezervasyon ekleyecektir. Onaylıyor musunuz?',
            type: 'info',
            confirmText: 'Evet, Oluştur',
            onConfirm: async () => {
                setGenerating(true);
                try {
                    await generateTestReservations(user.tenantId, 10);
                    success('Test verileri başarıyla oluşturuldu!');
                } catch (error) {
                    console.error(error);
                    toastError('Veri oluşturma hatası: ' + error.message);
                } finally {
                    setGenerating(false);
                }
            }
        });
    };

    // Form State with Defaults
    const [formData, setFormData] = useState({
        // Slot Settings
        slotDuration: 30, // minutes

        // Planning Units
        weightUnit: 'TON', // TON, KG
        volumeUnit: 'M3', // M3, L, PLT

        // Facility Operation Hours (Default 08:00 - 18:00)
        operationStartHour: '08:00',
        operationEndHour: '18:00',

        // Other globals
        currency: 'TRY',
        timezone: 'Europe/Istanbul',

        // Carrier Performance Weights (%)
        onTimeWeight: 40,
        accuracyWeight: 30,
        revisionWeight: 20,
        completionWeight: 10,
        toleranceMinutes: 15
    });

    useEffect(() => {
        if (user?.tenantId) {
            loadSettings();
        }
    }, [user?.tenantId]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const settings = await getGeneralSettings(user.tenantId);
            if (settings) {
                setExistingId(settings.id);
                setFormData({
                    slotDuration: settings.slotDuration || 30,
                    weightUnit: settings.weightUnit || 'TON',
                    volumeUnit: settings.volumeUnit || 'M3',
                    operationStartHour: settings.operationStartHour || '08:00',
                    operationEndHour: settings.operationEndHour || '18:00',
                    currency: settings.currency || 'TRY',
                    timezone: settings.timezone || 'Europe/Istanbul',
                    onTimeWeight: settings.onTimeWeight || 40,
                    accuracyWeight: settings.accuracyWeight || 30,
                    revisionWeight: settings.revisionWeight || 20,
                    completionWeight: settings.completionWeight || 10,
                    toleranceMinutes: settings.toleranceMinutes || 15
                });
            }
        } catch (err) {
            console.error(err);
            toastError('Ayarlar yüklenemedi.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await saveGeneralSettings(user.tenantId, existingId, formData);
            success('Sistem ayarları güncellendi.');
            // Reload to ensuring ID is set if it was a create operation
            loadSettings();
        } catch (err) {
            console.error(err);
            toastError('Kaydedilemedi: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper for generating time options
    const timeOptions = [];
    for (let i = 0; i < 24; i++) {
        const hour = i.toString().padStart(2, '0') + ':00';
        timeOptions.push(hour);
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h3 className="mb-xs">Genel Sistem Parametreleri</h3>
                    <p className="text-secondary text-sm">
                        Tüm sistemde geçerli olacak varsayılan yapılandırmalar.
                    </p>
                </div>
                <Button variant="primary" onClick={handleSave} loading={loading} style={{ minWidth: '120px' }}>Kaydet</Button>
            </div>

            <div className="card p-xl" style={{ border: '1px solid var(--border-color)' }}>

                {/* 1. SLOT SETTINGS */}
                <div className="mb-xl pb-lg border-b border-border">
                    <h4 className="text-lg font-bold mb-md flex items-center gap-sm">
                        <span>⏱️</span> Planlama & Zamanlama
                    </h4>
                    <div className="grid grid-cols-2 gap-lg">
                        <div className="form-group">
                            <label className="form-label">Varsayılan Slot Aralığı</label>
                            <select
                                className="input"
                                value={formData.slotDuration}
                                onChange={e => setFormData({ ...formData, slotDuration: parseInt(e.target.value) })}
                            >
                                <option value="15">15 Dakika</option>
                                <option value="30">30 Dakika</option>
                                <option value="45">45 Dakika</option>
                                <option value="60">60 Dakika</option>
                            </select>
                            <small className="text-secondary">Otomatik planlama önerilerinde ve takvim görünümünde kullanılacak temel süre.</small>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Zaman Dilimi</label>
                            <select
                                className="input"
                                value={formData.timezone}
                                onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                            >
                                <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                                <option value="Europe/London">Europe/London (UTC+0)</option>
                                <option value="Europe/Berlin">Europe/Berlin (UTC+1)</option>
                                <option value="US/Eastern">US/Eastern</option>
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-lg mt-md">
                            <div className="form-group">
                                <label className="form-label">Ağırlık Birimi</label>
                                <select
                                    className="input"
                                    value={formData.weightUnit}
                                    onChange={e => setFormData({ ...formData, weightUnit: e.target.value })}
                                >
                                    <option value="TON">TON</option>
                                    <option value="kg">kg</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Hacim Birimi</label>
                                <select
                                    className="input"
                                    value={formData.volumeUnit}
                                    onChange={e => setFormData({ ...formData, volumeUnit: e.target.value })}
                                >
                                    <option value="M3">m³</option>
                                    <option value="L">Litre</option>
                                    <option value="PLT">Palet</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-lg mt-md">
                            <div className="form-group">
                                <label className="form-label">Operasyon Başlangıç</label>
                                <select
                                    className="input"
                                    value={formData.operationStartHour}
                                    onChange={e => setFormData({ ...formData, operationStartHour: e.target.value })}
                                >
                                    {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Operasyon Bitiş</label>
                                <select
                                    className="input"
                                    value={formData.operationEndHour}
                                    onChange={e => setFormData({ ...formData, operationEndHour: e.target.value })}
                                >
                                    {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. DEVELOPER TOOLS */}
                <div className="pt-lg">
                    <h4 className="text-lg font-bold mb-md flex items-center gap-sm text-red-500">
                        <span>🛠️</span> Geliştirici Araçları
                    </h4>
                    <div className="p-md rounded bg-red-500/10 border border-red-500/20">
                        <p className="text-sm mb-md text-red-200">
                            Dikkat: Bu işlem sisteminize rastgele test verisi (Rezervasyonlar) ekleyecektir.
                            Sadece test/demo ortamında kullanınız.
                        </p>
                        <Button
                            variant="secondary"
                            onClick={handleGenerateData}
                            loading={generating}
                            style={{ borderColor: 'var(--status-cancelled)', color: 'var(--status-cancelled)' }}
                        >
                            ⚠️ Test Verisi Üret (10 Adet)
                        </Button>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default SystemParametersConfig;
